/**
 * SocialWordle host/spectator OBS browser entry point.
 *
 * Two modes:
 *   - host mode: `?key=<api_key>` → connects to wss://api.../browser?key=
 *     and shows the player QR pointing at play.thegamer.network/<handle>.
 *   - spectate mode: `?spectate=1&handle=<h>` (loaded from watch.* in an
 *     iframe) → connects to wss://api.../watch/<handle>/feed (read-only).
 *     Hides QR codes.
 *
 * The render layer is intentionally lightweight — single reducer (state.ts)
 * + a re-render hook per WS frame. No virtual DOM.
 */

import { applyFrame, initialState, type SwState } from './state.js';
import qrcodeFactory from './vendor/qrcode-generator/index.js';

const PUBLIC_API_HOST = (import.meta.env.VITE_PUBLIC_API_HOST as string | undefined) ?? 'https://api.thegamer.network';
const PUBLIC_PLAY_HOST =
  (import.meta.env.VITE_PUBLIC_PLAY_HOST as string | undefined) ?? 'https://play.thegamer.network';

const params = new URLSearchParams(window.location.search);
const mode: 'host' | 'spectator' = params.get('spectate') === '1' ? 'spectator' : 'host';
const apiKey = params.get('key');
const handleParam = params.get('handle');

const state: SwState = initialState();

const playersGridEl = document.getElementById('players-grid')!;
const leaderboardEl = document.getElementById('leaderboard')!;
const statusEl = document.getElementById('status')!;
const qrUrlEl = document.getElementById('qr-url')!;
const qrCanvasEl = document.getElementById('qr-canvas') as HTMLCanvasElement;
const splashEl = document.getElementById('splash')!;
const splashWordInfoEl = document.getElementById('splash-word-info')!;
const answerBannerEl = document.getElementById('answer-banner')!;
const answerWordEl = document.getElementById('answer-word')!;
const statPlayersEl = document.getElementById('stat-players')!;
const statWordlenEl = document.getElementById('stat-wordlen')!;
const headerTimerEl = document.getElementById('header-timer')!;
const timerLabelEl = document.getElementById('timer-label')!;
const timerValueEl = document.getElementById('timer-value')!;

// CSS-driven mode toggle: a `body.mode-spectate` class hides the QR + slims
// the header so the embedded watch.* iframe doesn't waste vertical space.
// Keeping the class on `body` (not on a wrapper element) lets the stylesheet
// scope every mode-specific rule cleanly via descendant selectors.
document.body.classList.add(mode === 'spectator' ? 'mode-spectate' : 'mode-host');

bootstrap();

// Header timer tick loop. Runs independently of the WS-frame-driven render
// so the seconds count down smoothly without re-rendering the (relatively
// expensive) players grid 4x/sec. 250ms cadence is fast enough to feel
// live but slow enough to be effectively free CPU-wise.
setInterval(renderHeaderTimer, 250);
// Render once immediately so the initial "Waiting for streamer..." shows
// without a one-tick delay on first paint.
renderHeaderTimer();

function bootstrap(): void {
  if (mode === 'spectator') {
    if (!handleParam) {
      statusEl.textContent = 'no handle';
      return;
    }
    connectSpectator(handleParam);
  } else {
    if (!apiKey) {
      statusEl.textContent = 'missing ?key=';
      return;
    }
    connectHost(apiKey);
  }
}

function connectHost(key: string): void {
  const wsOrigin = PUBLIC_API_HOST.replace(/^http/, 'ws');
  // `?game=socialwordle` is required so the backend registers THIS browser
  // source under the socialwordle slug. Without it, `extractGameSlug` defaults
  // to 'whatis' and cross-game routing (active-game registry + soft-abort
  // grace) misbehaves for multi-game tenants. See ws/auth-handshake.ts.
  const ws = new WebSocket(
    `${wsOrigin}/browser?key=${encodeURIComponent(key)}&game=socialwordle`,
  );
  wireSocket(ws);
  // The QR is stamped from the `auth_ok` frame (which carries `handle`).
  // Show a placeholder URL until that arrives.
  qrUrlEl.textContent = PUBLIC_PLAY_HOST.replace(/^https?:\/\//, '');
}

function connectSpectator(handle: string): void {
  const wsOrigin = PUBLIC_API_HOST.replace(/^http/, 'ws');
  const ws = new WebSocket(`${wsOrigin}/watch/${encodeURIComponent(handle)}/feed`);
  wireSocket(ws);
}

function wireSocket(ws: WebSocket): void {
  ws.addEventListener('open', () => {
    statusEl.textContent = 'connected';
    statusEl.classList.add('online');
  });
  ws.addEventListener('close', (ev) => {
    statusEl.textContent = `disconnected (${ev.code})`;
  });
  ws.addEventListener('message', (ev) => {
    try {
      const frame = JSON.parse(String(ev.data)) as { type: string } & Record<string, unknown>;
      // Intercept the auth handshake to stamp the QR with the streamer's
      // handle. The frame shape comes from `AuthMsg` in @sg/shared — we
      // duck-type here because this SPA doesn't depend on the shared pkg.
      if (mode === 'host' && frame.type === 'auth_ok') {
        const handle =
          typeof frame.handle === 'string' && frame.handle.length > 0
            ? frame.handle
            : null;
        stampHostQr(handle);
        return;
      }
      applyFrame(state, frame);
      render();
    } catch {
      // ignore malformed
    }
  });
}

/**
 * Render the QR card based on the streamer's claimed handle. If the streamer
 * hasn't claimed a handle yet, instruct them to do so in the dashboard
 * (the QR canvas stays blank — there's no useful target to encode).
 */
function stampHostQr(handle: string | null): void {
  if (!handle) {
    qrUrlEl.textContent = 'Claim a handle in your dashboard';
    const ctx = qrCanvasEl.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#1a1a24';
      ctx.fillRect(0, 0, qrCanvasEl.width, qrCanvasEl.height);
      ctx.fillStyle = '#8a91a6';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No handle yet', qrCanvasEl.width / 2, qrCanvasEl.height / 2);
    }
    return;
  }
  const playUrl = `${PUBLIC_PLAY_HOST}/${handle}`;
  qrUrlEl.textContent = playUrl.replace(/^https?:\/\//, '');
  renderQrCode(qrCanvasEl, playUrl);
}

function render(): void {
  renderStatusOverlay();
  renderHeaderStats();
  renderHeaderTimer();
  renderPlayers();
  renderLeaderboard();
}

function renderHeaderStats(): void {
  statPlayersEl.textContent = String(state.players.size);
  statWordlenEl.textContent = String(state.wordLength || 5);
}

/**
 * Render the header center timer. Three modes:
 *  - ROUND_ACTIVE: count down to `state.endsAtMs` (round-end). Label is
 *    "Time remaining"; the mm:ss number is brand-cyan and BIG.
 *  - WAITING_NEXT: count down to `state.nextRoundAtMs` (inter-round dwell
 *    set by sw_round_end). Label is "Next round in"; the number is white-ish.
 *  - IDLE: no active round, no scheduled restart — show a neutral
 *    "Waiting for streamer..." message in muted color.
 *
 * Always pinned at 0:00 minimum (never rolls negative). Format mm:ss with
 * zero-padded seconds.
 */
function renderHeaderTimer(): void {
  const now = Date.now();
  let mode: 'active' | 'waiting' | 'idle' = 'idle';
  let target: number | null = null;
  if (state.roundActive && typeof state.endsAtMs === 'number' && state.endsAtMs > 0) {
    mode = 'active';
    target = state.endsAtMs;
  } else if (typeof state.nextRoundAtMs === 'number' && state.nextRoundAtMs > 0) {
    mode = 'waiting';
    target = state.nextRoundAtMs;
  }

  // Reset class list to drive the mode-specific CSS.
  headerTimerEl.classList.remove('mode-active', 'mode-waiting', 'mode-idle');
  headerTimerEl.classList.add(`mode-${mode}`);

  if (mode === 'idle' || target === null) {
    timerLabelEl.textContent = '';
    timerValueEl.textContent = 'Waiting for streamer…';
    return;
  }

  const remainingMs = Math.max(0, target - now);
  timerValueEl.textContent = formatMmSs(remainingMs);
  timerLabelEl.textContent = mode === 'active' ? 'Time remaining' : 'Next round in';
}

function formatMmSs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

function renderStatusOverlay(): void {
  if (state.subState === 'GET_READY') {
    splashEl.classList.add('shown');
    splashWordInfoEl.textContent = `${state.wordLength}-letter word · ${state.maxTries} tries`;
  } else {
    splashEl.classList.remove('shown');
  }
  if (state.revealedWord && !state.roundActive) {
    answerBannerEl.classList.add('shown');
    answerWordEl.textContent = state.revealedWord;
    // Auto-hide after 4s so the next round's UI isn't blocked.
    setTimeout(() => answerBannerEl.classList.remove('shown'), 4_000);
  }
}

function renderPlayers(): void {
  playersGridEl.innerHTML = '';
  const rows = Array.from(state.players.values()).sort((a, b) => {
    if (a.solved !== b.solved) return a.solved ? -1 : 1;
    return a.triesUsed - b.triesUsed;
  });
  for (const p of rows) {
    const card = document.createElement('div');
    card.className = 'player-card';
    const status = p.solved
      ? `Solved in ${p.triesUsed}!`
      : p.failed
        ? 'Out of tries'
        : p.timedOut
          ? 'Out of time'
          : state.roundActive
            ? `Guess ${p.triesUsed + 1} of ${state.maxTries}`
            : 'Waiting…';
    card.innerHTML = `
      <div class="name">${escapeHtml(p.displayName)}</div>
      <div class="status">${escapeHtml(status)}</div>
      <div class="grid" id="grid-${escapeHtml(p.userId)}"></div>
    `;
    playersGridEl.appendChild(card);
    const grid = card.querySelector('.grid') as HTMLDivElement;
    grid.style.gridTemplateColumns = `repeat(${state.wordLength}, 1fr)`;
    for (const rowEvaluation of p.evaluations) {
      for (let c = 0; c < state.wordLength; c++) {
        const cell = document.createElement('div');
        cell.className = 'cell';
        const ev = rowEvaluation[c];
        if (ev) cell.classList.add(ev);
        grid.appendChild(cell);
      }
    }
  }
}

function renderLeaderboard(): void {
  leaderboardEl.innerHTML = '';
  for (const r of state.leaderboard.slice(0, 10)) {
    const div = document.createElement('div');
    div.className = 'lb-row';
    div.innerHTML = `<span class="rank">${r.rank}</span> <span>${escapeHtml(r.display_name)}</span> <span>${r.total_points}</span>`;
    leaderboardEl.appendChild(div);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/**
 * QR code renderer. Wraps the vendored `qrcode-generator` factory (see
 * ./vendor/qrcode-generator/). typeNumber 0 = auto-pick the smallest type
 * that fits the data; 'M' error correction = ~15% redundancy, balanced for
 * URL-length payloads. Lifted from `wordle-group/js/ui/qrcode.js`.
 */
function renderQrCode(canvas: HTMLCanvasElement, text: string, size = 160): void {
  const qr = qrcodeFactory(0, 'M');
  qr.addData(text);
  qr.make();

  const moduleCount = qr.getModuleCount();
  const margin = 1; // 1-cell quiet zone

  const cellSize = Math.max(2, Math.floor(size / (moduleCount + 2 * margin)));
  const pixelSize = (moduleCount + 2 * margin) * cellSize;

  canvas.width = pixelSize;
  canvas.height = pixelSize;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // White background (quiet zone included).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, pixelSize, pixelSize);

  // Dark modules.
  ctx.fillStyle = '#000000';
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (qr.isDark(row, col)) {
        ctx.fillRect(
          (col + margin) * cellSize,
          (row + margin) * cellSize,
          cellSize,
          cellSize,
        );
      }
    }
  }
}
